// PageDescription: 실시간 의료 상담 상세 페이지
import { dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import React from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import NotFoundTemplate from '@/components/templates/error/notFound/NotFoundTemplate';
import QnaDetailMobileTemplate from '@/components/templates/mobile/communication/Qna/QnaDetailMobileTemplate';
import QnaDetailSkeleton from '@/components/templates/mobile/communication/Qna/QnaDetailMobileTemplate/QnaDetailSkeleton';
import { SIX_HOURS_IN_MILLISECONDS } from '@/constants/common';
import useAppInstallTopBanner from '@/hooks/Cuj/useAppInstallTopBanner';
import useGetQnaDetailWithNearSimplyQuestions from '@/hooks/qna/useGetQnaDetailWithNearSimplyQuestions';
import useQuestionDetailViewModel from '@/hooks/qna/useQuestionDetailViewModel';
import useGetRelatedQna from '@/hooks/qna/useRelatedQna';
import useScreenFocus from '@/hooks/useScreenFocus';
import { ExtendedQueryClient } from '@/models/common/query-client';
import { ssrQueryCache } from '@/models/common/ssr-cache';
import { createQnaDetailLdJson } from '@/parsers/Communication/magazine';
import { CustomPageProps } from '@/types/app';
import { QuestionAccessibleType } from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { GraphqlError, ServerError } from '@/types/error';
import { formatShareUrl } from '@/utils/format';
import { NextSeo } from 'next-seo';
import Head from 'next/head';

interface Props extends CustomPageProps {
  id: string;
}

const QnaDetail = ({ id, client }: Props) => {
  useScreenFocus();
  const viewModel = useQuestionDetailViewModel();
  const topBannerViewModel = useAppInstallTopBanner();

  if (viewModel.isQnaDetailLoading) {
    return <QnaDetailSkeleton />;
  }

  if (!viewModel.qnaDetail) return <NotFoundTemplate />;

  if (viewModel.isNotMine && viewModel.isPrivateQuestion) {
    return <NotFoundTemplate />;
  }

  const ldJson = createQnaDetailLdJson(viewModel.qnaDetail);

  return (
    <>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />
      </Head>
      <NextSeo
        noindex={viewModel.isPrivateQuestion}
        nofollow={viewModel.isPrivateQuestion}
        title={`${viewModel.qnaDetail.title} - 닥터나우`}
        description={`${viewModel.qnaDetail.content}`}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.QNA.DETAIL.path}/${id}`}
        openGraph={{
          title: `${viewModel.qnaDetail.title} - 닥터나우`,
          description: `${viewModel.qnaDetail.content}`,
          url: `${formatShareUrl(ROUTES.QNA.HOME.path)}/${id}`,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_QNA_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <QnaDetailMobileTemplate
        viewModel={{ ...viewModel, qnaDetail: viewModel.qnaDetail }}
        client={client}
        topBannerViewModel={topBannerViewModel}
      />
    </>
  );
};

QnaDetail.Layout = DefaultLayout;

export default QnaDetail;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const id = ctx.params?.id as string;

  if (!id) {
    return {
      redirect: {
        destination: '/content/qna',
        permanent: false,
      },
    };
  }

  const queryClient = new ExtendedQueryClient();

  const cacheKey = ctx.req.url as string;

  try {
    const questionDetail = await useGetQnaDetailWithNearSimplyQuestions.fetchQuery(queryClient, id);

    if (questionDetail.questionV2?.accessibleType !== QuestionAccessibleType.All) {
      return {
        props: {
          id,
        },
      };
    }

    if (ssrQueryCache.has(cacheKey)) {
      queryClient.setQueryDataFromCache(ssrQueryCache.get(cacheKey) ?? []);

      return {
        props: {
          id,
          dehydratedState: dehydrate(queryClient),
        },
      };
    }

    await useGetRelatedQna.prefetchQuery(queryClient, id);
  } catch (e: unknown) {
    const { code } = e as unknown as GraphqlError;
    if (code === ServerError.NotFound) {
      return {
        notFound: true,
      };
    }
    if (ctx.res && code === ServerError.BadRequest) {
      ctx.res.statusCode = 400;
    }

    return {
      notFound: true,
    };
  }

  ssrQueryCache.set(cacheKey, queryClient.getQueryClientData([useGetRelatedQna.getKey(id)]), SIX_HOURS_IN_MILLISECONDS);

  return {
    props: {
      id,
      dehydratedState: dehydrate(queryClient),
    },
  };
};
